// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import saveCurrentAccount from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SaveCurrentAccount";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
    // XNG-Change
    saveCurrentAccount,
};

const mapStateToProps = (state: ApplicationState) => ({
    account: state.pages.accountSettings.editingAccount,
    allowSubscribeToNewsLetter: getSettingsCollection(state).accountSettings.allowSubscribeToNewsLetter,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsManageSubscriptionsStyles {
    manageSubscriptionsCheckboxGroup: CheckboxGroupComponentProps;
    manageSubscriptionsCheckbox: CheckboxProps;
    manageSubscriptionsTitle: TypographyProps;
    // XNG-Changes
    manageSubscriptionsRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
}

export const manageSubscriptionsStyles: AccountSettingsManageSubscriptionsStyles = {
    manageSubscriptionsTitle: {
        variant: "h2",
        as: "h2",
    },
    manageSubscriptionsCheckbox: {
        typographyProps: {
            css: css`
                margin-left: 10px;
            `,
        },
    },
    manageSubscriptionsCheckboxGroup: {},
};

const styles = manageSubscriptionsStyles;
const SUBSCRIBE = "Subscribe";
const UNSUBSCRIBE = "Unsubscribe";

const AccountSettingsManageSubscriptions = ({
    allowSubscribeToNewsLetter,
    account,
    updateAccountSettings,
    // XNG-Changes
    saveCurrentAccount,
}: Props) => {
    // XNG-Changes
    const toasterContext = useContext(ToasterContext);

    if (!allowSubscribeToNewsLetter || !account) {
        return null;
    }

    // XNG-Changes:change to match classic
    const subscriptionChangeHandler = (e: any) => {
        updateAccountSettings({ isSubscribed: e.target.value === SUBSCRIBE });
        saveCurrentAccount({
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Settings updated"), messageType: "success" });
            },
            onError: (message: any) => {
                toasterContext.addToast({ body: message, messageType: "warning" });
            },
        });
    };

    // XNG-Changes: add StyledWrapper to match the design use Radio Group
    return (
        <StyledWrapper>
            <Typography {...styles.manageSubscriptionsTitle}>{translate("Manage Subscriptions")}</Typography>
            {/* <CheckboxGroup {...styles.manageSubscriptionsCheckboxGroup}>
                <Checkbox
                    {...styles.manageSubscriptionsCheckbox}
                    onChange={(_, value) => updateAccountSettings({ isSubscribed: value })}
                    checked={account.isSubscribed!}
                    data-test-selector="accountSettings_subscribed"
                >
                    {translate("Subscribe")}
                </Checkbox>
            </CheckboxGroup> */}

            <RadioGroup
                as="legend"
                value={account.isSubscribed! ? SUBSCRIBE : UNSUBSCRIBE}
                onChangeHandler={(e: any) => subscriptionChangeHandler(e)}
                {...styles.manageSubscriptionsRadioGroup}
            >
                <Radio value={SUBSCRIBE}>{translate("Unsubscribe")}</Radio>
                <Radio value={UNSUBSCRIBE}>{translate("Subscribe")}</Radio>
            </RadioGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsManageSubscriptions),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "Manage Subscriptions",
    },
};

export default widgetModule;
